import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { akEditorSelectedNodeClassName } from '@atlaskit/editor-shared-styles/constants';
import type { EditorCommand } from '@atlaskit/editor-common/types/editor-command';
import type { NextEditorPlugin } from '@atlaskit/editor-common/types/next-editor-plugin';
import { PluginKey, TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

const FLOATING_TABLE_OF_CONTENTS = 'floatingTableOfContents';

const SELECT_HEADING_META_TYPE = 'selectHeading';
const CLEAR_SELECTED_HEADING_META_TYPE = 'clearSelectedHeading';

type PluginMeta =
	| {
			type: typeof SELECT_HEADING_META_TYPE;
			position: number;
	  }
	| {
			type: typeof CLEAR_SELECTED_HEADING_META_TYPE;
	  };

const pluginKey = new PluginKey<DecorationSet>(FLOATING_TABLE_OF_CONTENTS);

const getPluginMeta = (tr: Transaction): PluginMeta | undefined => {
	const meta: PluginMeta | undefined = tr.getMeta(pluginKey);

	if (!meta) {
		return undefined;
	}

	switch (meta.type) {
		case CLEAR_SELECTED_HEADING_META_TYPE:
			return { type: CLEAR_SELECTED_HEADING_META_TYPE };
		case SELECT_HEADING_META_TYPE:
			return { type: SELECT_HEADING_META_TYPE, position: meta.position };
		default:
			return undefined;
	}
};

const createSelectedHeadingDecorations = (state: EditorState, position: number): DecorationSet => {
	const heading = state.doc.nodeAt(position);
	if (heading?.type.name !== 'heading') {
		return DecorationSet.empty;
	}

	return DecorationSet.create(state.doc, [
		Decoration.node(position, position + heading.nodeSize, {
			class: akEditorSelectedNodeClassName,
		}),
	]);
};

const applyPluginState = (
	tr: Transaction,
	decorations: DecorationSet,
	_oldState: EditorState,
	newState: EditorState,
): DecorationSet => {
	const meta = getPluginMeta(tr);

	switch (meta?.type) {
		case SELECT_HEADING_META_TYPE:
			return createSelectedHeadingDecorations(newState, meta.position);
		case CLEAR_SELECTED_HEADING_META_TYPE:
			return DecorationSet.empty;
		default:
			return tr.docChanged ? DecorationSet.empty : decorations;
	}
};

const getDecorations = (state: EditorState): DecorationSet =>
	pluginKey.getState(state) ?? DecorationSet.empty;

const handleEditorClick = (view: EditorView): boolean => {
	const decorations = pluginKey.getState(view.state);
	if (decorations && decorations !== DecorationSet.empty) {
		const clearSelectedHeadingMeta: PluginMeta = {
			type: CLEAR_SELECTED_HEADING_META_TYPE,
		};
		view.dispatch(view.state.tr.setMeta(pluginKey, clearSelectedHeadingMeta));
	}

	return false;
};

const selectHeading =
	(position: number): EditorCommand =>
	({ tr }) => {
		const heading = tr.doc.nodeAt(position);
		const selectionPosition = position + 1;
		if (heading?.type.name !== 'heading' || selectionPosition > tr.doc.content.size) {
			return null;
		}

		const selectHeadingMeta: PluginMeta = {
			type: SELECT_HEADING_META_TYPE,
			position,
		};

		return tr
			.setSelection(TextSelection.create(tr.doc, selectionPosition))
			.setMeta(pluginKey, selectHeadingMeta)
			.setMeta('scrollIntoView', false);
	};

export type FloatingTableOfContentsPlugin = NextEditorPlugin<
	typeof FLOATING_TABLE_OF_CONTENTS,
	{
		commands: {
			selectHeading: (position: number) => EditorCommand;
		};
	}
>;

export const floatingTableOfContentsPlugin: FloatingTableOfContentsPlugin = () => ({
	name: FLOATING_TABLE_OF_CONTENTS,
	commands: {
		selectHeading,
	},
	pmPlugins() {
		return [
			{
				name: 'floatingTableOfContentsPlugin',
				plugin: () =>
					new SafePlugin<DecorationSet>({
						key: pluginKey,
						state: {
							init: () => DecorationSet.empty,
							apply: applyPluginState,
						},
						props: {
							decorations: getDecorations,
							handleDOMEvents: {
								click: handleEditorClick,
							},
						},
					}),
			},
		];
	},
});
